import React from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { ArrowRight, Calendar, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { Card } from '../components/ui/Card';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
export function ServicesPage() {
  const {
    t,
    lang
  } = useTranslation();
  const seoTitle = lang === 'en' ? 'Dental Services in Angel, London | Dentico Clinic' : lang === 'ua' ? 'Стоматологічні послуги в Angel, Лондон | Dentico Clinic' : lang === 'ro' ? 'Servicii Dentare în Angel, Londra | Dentico Clinic' : 'Стоматологические услуги в Angel, Лондон | Dentico Clinic';
  const seoDescription = lang === 'en' ? 'Explore private dental services at Dentico Clinic in Angel, including hygiene, fillings, veneers, implants, orthodontics and emergency care.' : lang === 'ua' ? 'Ознайомтеся з приватними стоматологічними послугами в Dentico Clinic в Angel, включаючи гігієну, пломби, вініри, імпланти, ортодонтію та невідкладну допомогу.' : lang === 'ro' ? 'Explorați serviciile dentare private la Dentico Clinic din Angel, inclusiv igienă, plombe, fațete, implanturi, ortodonție și îngrijire de urgență.' : 'Ознакомьтесь с частными стоматологическими услугами в Dentico Clinic в Angel, включая гигиену, пломбы, виниры, импланты, ортодонтию и неотложную помощь.';
  const services = [{
    id: 'fillings',
    title: t('services.fillings.title'),
    description: t('services.fillings.desc'),
    image: 'https://drive.google.com/thumbnail?id=1GoYPTv2Ah2lfohOV_wcv06DwMvJ8-YIf&sz=w1200'
  }, {
    id: 'root-canal',
    title: t('services.rootCanal.title'),
    description: t('services.rootCanal.desc'),
    image: 'https://drive.google.com/thumbnail?id=1oecjcYpi0WMZbrhKFXRxj1Z597Vlnj1m&sz=w1200'
  }, {
    id: 'crowns',
    title: t('services.crowns.title'),
    description: t('services.crowns.desc'),
    image: 'https://cdn.magicpatterns.com/uploads/ns891AKGuZCaDei6mehiRt/ChatGPT_Image_Sep_28,_2026,_08_24_38_PM-2.png'
  }, {
    id: 'hygiene',
    title: t('services.hygiene.title'),
    description: t('services.hygiene.desc'),
    image: 'https://cdn.magicpatterns.com/uploads/4h5wQ8THmt6sSAX8agbB73/ChatGPT_Image_Sep_28,_2026,_08_24_39_PM-3.png'
  }, {
    id: 'whitening',
    title: t('services.whitening.title'),
    description: t('services.whitening.desc'),
    image: 'https://cdn.magicpatterns.com/uploads/4kRqTAaJ1u6tJq3cB7gSSs/ChatGPT_Image_Sep_28,_2026,_08_24_36_PM-1.png'
  }, {
    id: 'extraction',
    title: t('services.extraction.title'),
    description: t('services.extraction.desc'),
    image: 'https://drive.google.com/thumbnail?id=1H9Sf4ZxWwyVlbdh7GXl7iGQTbjS1U2mD&sz=w1200'
  }, {
    id: 'smile-makeover',
    title: t('services.smileMakeover.title'),
    description: t('services.smileMakeover.desc'),
    image: 'https://cdn.magicpatterns.com/uploads/aws3cmiUVaQNLxKjpQRh73/IMG_3720.jpg'
  }, {
    id: 'cosmetic',
    title: t('services.cosmetic.title'),
    description: t('services.cosmetic.desc'),
    image: 'https://drive.google.com/thumbnail?id=1WfQHZfd0cw_7cJJrW6R75IqRiZXZJ9Yb&sz=w1200'
  }, {
    id: 'paediatric-dentistry',
    title: t('services.paediatric.title'),
    description: t('services.paediatric.desc'),
    image: 'https://drive.google.com/thumbnail?id=1EkX5ZapNGT_ssdlSh1hBhv8Yym2-cS-j&sz=w1200'
  }];
  const pick = (en: string, ua: string, ru: string, ro: string) => lang === 'ua' ? ua : lang === 'ru' ? ru : lang === 'ro' ? ro : en;
  // Category sections — homepage cards link to these anchors (#general-dentistry, #advanced-treatments)
  const serviceGroups = [{
    id: 'general-dentistry',
    title: pick('General dentistry', 'Загальна стоматологія', 'Общая стоматология', 'Stomatologie generală'),
    intro: pick('Hygiene, fillings and children’s dentistry to keep teeth healthy.', 'Гігієна, пломби та дитяча стоматологія для здоров’я зубів.', 'Гигиена, пломбы и детская стоматология для здоровья зубов.', 'Igienă, plombe și stomatologie pediatrică pentru dinți sănătoși.'),
    serviceIds: ['fillings', 'hygiene', 'paediatric-dentistry']
  }, {
    id: 'advanced-treatments',
    title: pick('Advanced treatments', 'Складні методи лікування', 'Сложные методы лечения', 'Tratamente avansate'),
    intro: pick('Root canal treatment, crowns and extractions for more complex cases.', 'Лікування каналів, коронки та видалення зубів у складніших випадках.', 'Лечение каналов, коронки и удаление зубов в более сложных случаях.', 'Tratament de canal, coroane și extracții pentru cazuri mai complexe.'),
    serviceIds: ['root-canal', 'crowns', 'extraction']
  }, {
    id: 'cosmetic-dentistry',
    title: pick('Cosmetic dentistry', 'Естетична стоматологія', 'Эстетическая стоматология', 'Stomatologie estetică'),
    intro: pick('Whitening, smile makeovers and cosmetic treatments.', 'Відбілювання, преображення усмішки та естетичні процедури.', 'Отбеливание, преображение улыбки и эстетические процедуры.', 'Albire, transformarea zâmbetului și tratamente estetice.'),
    serviceIds: ['whitening', 'smile-makeover', 'cosmetic']
  }];
  return <Layout title={seoTitle} description={seoDescription} data-id="element-4093" data-mp="3tezg8328eiem">
      <div className="bg-forest-900 py-16 lg:py-20 text-white" data-id="element-4094" data-mp="3tehfat4mjg0d">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-4095" data-mp="3ofmj8phnbuyb">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-4096" data-mp="3esjzb3qp3a3w">
            {t('servicesPage.title')}
          </h1>
          <p className="text-xl text-ivory/80 max-w-2xl mx-auto" data-id="element-4097" data-mp="3jrwohv51gt9n">
            {t('servicesPage.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 space-y-16 lg:space-y-20" data-id="element-4098" data-mp="35tezzv7b7cqo">
        {serviceGroups.map(group => <section key={group.id} id={group.id} aria-labelledby={`${group.id}-heading`} className="scroll-mt-28" data-id="element-4099" data-mp="1cvsonkexd2u">
            <div className="max-w-2xl mb-8" data-id="element-4100" data-mp="6ebdceiohhkp">
              <h2 id={`${group.id}-heading`} className="text-2xl md:text-3xl font-bold text-forest-900 mb-2" data-id="element-4101" data-mp="3opeylouslej8">
                {group.title}
              </h2>
              <p className="text-charcoal-light" data-id="element-4102" data-mp="kbgfi3f9twao">{group.intro}</p>
            </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-4103" data-mp="p3896mtq4i51">
          {group.serviceIds.map(id => services.find(service => service.id === id)).filter((service): service is (typeof services)[number] => Boolean(service)).map(service => <Card key={service.id} hover className="overflow-hidden flex flex-col h-full border-0 shadow-lg" data-id="element-4104" data-mp="2jhvjp6nak9yb">
              <Link to={`/${lang}/services/${service.id}`} className="block" data-id="element-4105" data-mp="1qu9k09h6ppgq">
                <div className="relative aspect-[4/3] overflow-hidden" data-id="element-4106" data-mp="1qwcdx4lgk2qq">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-4107" data-mp="1lx9b7qsya8lx" />
                </div>
              </Link>

              <div className="p-6 flex flex-col flex-grow" data-id="element-4108" data-mp="3c6t3je75iet6">
                <Link to={`/${lang}/services/${service.id}`} data-id="element-4109" data-mp="378rgki6ltvcf">
                  <h3 className="text-xl font-bold text-charcoal mb-3 hover:text-forest-600 transition-colors" data-id="element-4110" data-mp="378hs4z17jw8u">
                    {service.title}
                  </h3>
                </Link>
                <p className="text-charcoal-light mb-6 text-sm line-clamp-2 flex-grow" data-id="element-4111" data-mp="19lqcain8rbv4">
                  {service.description}
                </p>

                <div className="flex gap-3 mt-auto" data-id="element-4112" data-mp="1jaqiam05v7it">
                  <Link to={`/${lang}/services/${service.id}`} className="flex-1" data-id="element-4113" data-mp="1jcb9q2r6iacg">
                    <Button variant="outline" size="sm" fullWidth data-id="element-4114" data-mp="1sbev5ddtj1u4">
                      {t('services.learnMore')}
                    </Button>
                  </Link>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="flex-1" data-id="element-4115" data-mp="1x8or7bvpqbqx">
                    <Button size="sm" fullWidth data-id="element-4116" data-mp="2yutacxmth5c">
                      {t('nav.bookOnline')}
                    </Button>
                  </a>
                </div>
              </div>
            </Card>)}
        </div>
          </section>)}
      </div>

      <div className="bg-ivory-200 py-16 lg:py-20" data-id="element-4117" data-mp="3rpf31bvvvj48">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-4118" data-mp="3mrnaqgrbtuzi">
          <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-4119" data-mp="3ms6ij4n9j2zt">
            {t('servicesPage.readyToBook')}
          </h2>
          <p className="text-charcoal-light mb-8" data-id="element-4120" data-mp="jb6gakg1lmjz">
            {t('servicesPage.readyToBookDesc')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-4121" data-mp="ecd4cwgem3lx">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-4122" data-mp="ea90ufmf36m6">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-4123" data-mp="9b5umrupaucm">
                <Calendar className="w-5 h-5" data-id="element-4124" data-mp="4cvyof15jppy" />
                {t('servicesPage.bookOnlineNow')}
              </Button>
            </a>
            <a href="https://wa.me/447345197820" target="_blank" rel="noopener noreferrer" data-id="element-4125" data-mp="2u4izhu1n4kfa">
              <Button variant="whatsapp" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-4126" data-mp="2w4ivzuzbf82s">
                WhatsApp
              </Button>
            </a>
            <Link to={`/${lang}/contact`} data-id="element-4127" data-mp="35vx1cjdy723u">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" data-id="element-4128" data-mp="35wyecteixyo7">
                {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : lang === 'ru' ? 'Заказать звонок' : 'Solicitați un Apel'}
              </Button>
            </Link>
            <Link to={`/${lang}/prices`} data-id="element-4129" data-mp="3jvoao6tirubi">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" data-id="element-4130" data-mp="3jsjx4iu5zm86">
                {lang === 'en' ? 'View Prices' : lang === 'ua' ? 'Переглянути ціни' : lang === 'ru' ? 'Посмотреть цены' : 'Vedeți Prețurile'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>;
}